import { SerializedError, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	ConversationSummary,
	DirectMessage,
	FullDirectMessage,
	User,
	dateDiffMilliseconds,
	getNow,
	addMilliseconds,
	addDays
} from 'model';
import { HttpRequest } from '../App.js';

export type DirectMessageState = FullDirectMessage & {
	requestId?: string;
	error?: SerializedError;
	deleting?: boolean;
};
export type SummaryState = ConversationSummary & {
	loading?: boolean;
	error?: SerializedError;
	next?: string; // next time to refresh
};

export type ConversationState = {
	user?: User['_id'];
	messages: DirectMessageState[];
	loading?: boolean;
	next?: string; // next time to refresh
	start?: string; // start of message history (may not be the same as messages[0].updated)
	loadingPrevious?: boolean;
	loadingInitial?: boolean;
};
export interface MessagesState {
	summary: SummaryState;
	conversation: ConversationState;
	pollTimer?: ReturnType<typeof setInterval>;
}

const initialState: MessagesState = {
	summary: {
		totalUnread: 0,
		conversations: [],
		loading: false
	},
	conversation: {
		messages: []
	}
};

type ThunkAPI = Parameters<Parameters<typeof createAsyncThunk>[1]>[1];

/**
 * Given the last update time return the next interval to refresh messages
 * @param { string } lastUpdate ISO8601 time of last update
 * @returns { number } number of milliseconds for next interval
 */
function getNextInterval(lastUpdate?: string): number {
	if (!lastUpdate) {
		return 5000;
	}
	const delta = dateDiffMilliseconds(lastUpdate, getNow());
	if (delta <= 60000) {
		return 5000;
	} else if (delta <= 120000) {
		return 15000;
	} else if (delta <= 300000) {
		return 30000;
	}
	return 120000;
}

/**
 * Get the latest update time given a list of messages
 * @param { FullDirectMessage[] } messages Array of messages
 * @returns { string } the ISO8601 time of the latest updated message in the list
 */
function getLatestMessageUpdateTime(messages: FullDirectMessage[]): string {
	return messages.reduce((accum: string, { updated }) => {
		if (updated > accum) {
			return updated;
		}
		return accum;
	}, '');
}

/**
 * Interval scheduling handler
 * @param { ThunkAPI } api the thunk api object passed into an async thunk
 * @returns { void }
 */
function intervalTick(api: ThunkAPI): void {
	const state = (api.getState() as { messages: MessagesState }).messages as MessagesState;
	const now = getNow();
	if (
		!state.summary.loading &&
		state.summary.next && // Test if the next refresh is scheduled
		dateDiffMilliseconds(now, state.summary.next) <= 0
	) {
		api.dispatch(refreshSummary());
	}
	if (
		!state.conversation.loading &&
		state.conversation?.user && // Don't refresh if no user
		state.conversation?.next && // Test if the next refresh is scheduled
		dateDiffMilliseconds(now, state.conversation.next) <= 0 // Test if now - (last + next) <= 0
	) {
		// refresh the latest
		api.dispatch(fetchLatest(state.conversation.user));
	}
}

export const startPolling = createAsyncThunk(
	'messages/startPolling',
	async (arg: void | undefined, api): Promise<ReturnType<typeof setInterval>> => {
		const current = (api.getState() as MessagesState).pollTimer;
		if (current) {
			clearInterval(current);
		}
		return setInterval(() => intervalTick(api), 10);
	}
);

export const stopPolling = createAsyncThunk(
	'messages/stopPolling',
	async (arg: void | undefined, api): Promise<void> => {
		const current = (api.getState() as MessagesState).pollTimer;
		if (current) {
			clearInterval(current);
		}
	}
);

export const refreshSummary = createAsyncThunk('messages/setSummary', async (): Promise<ConversationSummary> => {
	const response = await HttpRequest({
		url: '/api/directMessage/',
		method: 'GET'
	});
	if (response.status === 200) {
		return response.data;
	}
	throw new Error(await response.data);
});

export const fetchLatest = createAsyncThunk('messages/fetchLatest', async (user: string): Promise<DirectMessage[]> => {
	const response = await HttpRequest({
		url: `/api/directMessage/conversation/${user}`,
		method: 'GET',
		headers: {
			'content-type': 'application/json'
		}
	});
	if (response.status === 200) {
		return response.data;
	} else if (response.status === 404) {
		return [];
	} else {
		throw new Error(response.data);
	}
});

export const fetchPrevious = createAsyncThunk(
	'messages/fetchPrevious',
	async (arg: void | undefined, api): Promise<DirectMessage[]> => {
		const { conversation }: MessagesState = (api.getState() as { messages: MessagesState }).messages;
		if (!conversation.user) {
			throw new Error('Cannot fetch previous with no user set');
		}
		if (!conversation.start) {
			return [];
		}
		const end = conversation.start;
		const start = addDays(end, -30);
		const response = await HttpRequest({
			url: `/api/directMessage/conversation/${conversation.user}/${start}/${end}`,
			method: 'GET',
			headers: {
				'content-type': 'application/json'
			}
		});
		if (response.status === 200) {
			return response.data;
		} else if (response.status === 404) {
			return [];
		} else {
			throw new Error(response.data);
		}
	}
);

export interface SendMessagesParams {
	to: User['_id'];
	from: User['_id'];
	message: string;
	image?: any;
}

export const sendMessage = createAsyncThunk(
	'messages/sendMessage',
	async (data: DirectMessage): Promise<DirectMessage> => {
		const response = await HttpRequest({
			url: `/api/directMessage/`,
			method: 'POST',
			headers: {
				'content-type': 'application/json'
			},
			data
		});

		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error(response.data);
		}
	}
);

export const fetchMessage = createAsyncThunk(
	'messages/fetchMessage',
	async (data: DirectMessage['_id']): Promise<DirectMessage> => {
		const response = await HttpRequest({
			url: `/api/directMessage/${data}`,
			method: 'GET',
			headers: {
				'content-type': 'application/json'
			}
		});

		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error(response.data);
		}
	}
);

export const updateMessage = createAsyncThunk(
	'messages/updateMessage',
	async (data: DirectMessage): Promise<DirectMessage> => {
		if (!data._id) {
			throw new Error('Cannot update a pending message.');
		}
		const response = await HttpRequest({
			url: `/api/directMessage/${data._id}`,
			method: 'PUT',
			headers: {
				'content-type': 'application/json'
			},
			data
		});

		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error(response.data);
		}
	}
);

export const deleteMessage = createAsyncThunk(
	'messages/deleteMessage',
	async (id: DirectMessage['_id']): Promise<DirectMessage> => {
		if (!id) {
			throw new Error('Mesage id is required');
		}
		const response = await HttpRequest({
			url: `/api/directMessage/${id}`,
			method: 'DELETE',
			headers: {
				'content-type': 'application/json'
			}
		});

		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error(response.data);
		}
	}
);

export const trackReadMessage = createAsyncThunk(
	'messages/trackReadMessage',
	async (id: DirectMessage['_id']): Promise<DirectMessage> => {
		if (!id) {
			throw new Error('Mesage id is required');
		}
		const response = await HttpRequest({
			url: `/api/directMessage/${id}/read`,
			method: 'PUT',
			headers: {
				'content-type': 'application/json'
			}
		});

		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error(response.data);
		}
	}
);
/**
 * Merges an incoming list of messages with the current list, handles deletes, updates etc
 * @param {DirectMessage[] | undefined} current the current list of messages
 * @param {DirectMessage[]} incoming the incoming list of messages
 * @returns {DirectMessage[]} the merged list of messages
 */
function mergeMessages(current: DirectMessage[] | undefined, incoming: DirectMessage[]): DirectMessage[] {
	if (current === undefined) {
		return incoming;
	}
	// Build index of incoming message ids
	const incomingIndex: Partial<Record<string, boolean>> = incoming.reduce((accum, message) => {
		accum[message._id] = true;
		return accum;
	}, {} as Partial<Record<string, boolean>>);
	// Filter updated entries out of current and concat current & incoming then sort by created
	return current
		.filter(({ _id }) => !incomingIndex[_id])
		.concat(incoming)
		.filter(({ deleted }) => !deleted)
		.sort((a, b) => (a.created === b.created ? 0 : a.created > b.created ? 1 : -1));
}

const messagesSlice = createSlice({
	name: 'messages',
	initialState,
	reducers: {
		setUser: (state: MessagesState, { payload }: { payload: User['_id'] | undefined }) => {
			if (state.conversation.user === payload) {
				return state;
			}
			state.conversation.loadingInitial = undefined;
			state.conversation.user = payload;
			state.conversation.messages = [];
			state.conversation.loading = false;
			state.conversation.start = undefined;
			state.conversation.next = state.conversation.user ? getNow() : undefined;
			state.conversation.loadingPrevious = undefined;
		},
		reportMessage: (state: MessagesState, { payload: id }: { payload: DirectMessage['_id'] }): MessagesState => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === id) {
					state.conversation.messages[i].reported = true;
					return state;
				}
			}
			return state;
		},
		unreportMessage: (state: MessagesState, { payload: id }: { payload: DirectMessage['_id'] }): MessagesState => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === id) {
					state.conversation.messages[i].reported = false;
					return state;
				}
			}
			return state;
		}
	},
	extraReducers: builder => {
		builder.addCase(startPolling.fulfilled, (state, action) => {
			state.summary.next = getNow();
		});
		// Add reducers for additional action types here, and handle loading state as needed
		builder.addCase(refreshSummary.fulfilled, (state, action) => {
			state.summary = action.payload;
			state.summary.next = addMilliseconds(getNow(), 60000);
			state.summary.loading = false;
		});
		builder.addCase(refreshSummary.rejected, (state, action) => {
			state.summary.next = addMilliseconds(getNow(), 60000);
			state.summary.error = action.error;
			state.summary.loading = false;
		});
		builder.addCase(refreshSummary.pending, (state, action) => {
			// clear next
			state.summary.next = undefined;
			state.summary.loading = true;
			state.summary.error = undefined;
		});

		// fetchLatest
		builder.addCase(fetchLatest.fulfilled, (state, action) => {
			state.conversation.loadingInitial = false;
			state.conversation.messages = mergeMessages(
				state.conversation.messages.filter(message => message._id !== ''),
				action.payload
			);
			state.conversation.loading = false;
			state.conversation.next = addMilliseconds(
				getNow(),
				getNextInterval(getLatestMessageUpdateTime(state.conversation.messages))
			);
			state.conversation.start = state.conversation.start ?? state.conversation.messages[0]?.created ?? getNow();
			// this is odd, but prevents loading the previous before latest has been fetched (atTopStateChange fires when there are no items triggering this)
			if (state.conversation.loadingPrevious === undefined) {
				state.conversation.loadingPrevious = false;
			}
		});
		builder.addCase(fetchLatest.rejected, (state, action) => {
			state.conversation.loadingInitial = false;
			state.conversation.loading = false;
			state.conversation.next = addMilliseconds(
				getNow(),
				getNextInterval(getLatestMessageUpdateTime(state.conversation.messages))
			);
		});
		builder.addCase(fetchLatest.pending, (state, action) => {
			state.conversation.loadingInitial = state.conversation.loadingInitial === undefined;
			state.conversation.loading = true;
			state.conversation.next = undefined; // next is scheduled upon rejection/fulfillment
		});

		// fetchPrevious
		builder.addCase(fetchPrevious.fulfilled, (state, action) => {
			state.conversation.messages = mergeMessages(
				state.conversation.messages.filter(message => message._id !== ''),
				action.payload
			);
			state.conversation.loadingPrevious = false;
			state.conversation.next = addMilliseconds(
				getNow(),
				getNextInterval(getLatestMessageUpdateTime(state.conversation.messages))
			);
		});
		builder.addCase(fetchPrevious.rejected, (state, action) => {
			state.conversation.loadingPrevious = false;
			state.conversation.next = addMilliseconds(
				getNow(),
				getNextInterval(getLatestMessageUpdateTime(state.conversation.messages))
			);
		});
		builder.addCase(fetchPrevious.pending, (state, action) => {
			state.conversation.loadingPrevious = true;
			state.conversation.start = state.conversation.start
				? addDays(state.conversation.start, -30)
				: addDays(getNow(), -30);
		});

		// sendMessage
		builder.addCase(sendMessage.fulfilled, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i].requestId === action.meta.requestId) {
					state.conversation.messages[i] = action.payload;
					return;
				}
			}
		});
		builder.addCase(sendMessage.rejected, (state, action) => {
			state.conversation.messages = state.conversation.messages.filter(
				({ requestId }) => requestId === action.meta.requestId
			);
			const now = getNow();
			state.conversation.next = addMilliseconds(now, getNextInterval(now));
		});
		builder.addCase(sendMessage.pending, (state, action) => {
			state.conversation.messages.push({
				...action.meta.arg,
				requestId: action.meta.requestId
			});
			const now = getNow();
			state.conversation.next = addMilliseconds(now, getNextInterval(now));
		});

		// updateMessage
		builder.addCase(updateMessage.fulfilled, (state, action) => {
			state.conversation.next = state.conversation.user ? getNow() : undefined;
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.payload._id) {
					state.conversation.messages[i] = action.payload;
					return;
				}
			}
			const now = getNow();
			state.conversation.next = addMilliseconds(now, getNextInterval(now));
		});
		builder.addCase(updateMessage.rejected, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg._id) {
					// clear the request id and set the error
					state.conversation.messages[i].requestId = undefined;
					state.conversation.messages[i].error = action.error;
					return;
				}
			}
			const now = getNow();
			state.conversation.next = addMilliseconds(now, getNextInterval(now));
		});
		builder.addCase(updateMessage.pending, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg._id) {
					// set request id and new message
					state.conversation.messages[i].requestId = action.meta.requestId;
					state.conversation.messages[i].message = action.meta.arg.message;
					return;
				}
			}
		});

		// fetchMessage
		builder.addCase(fetchMessage.fulfilled, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i].requestId === action.meta.requestId) {
					state.conversation.messages[i] = action.payload;
					return;
				}
			}
		});
		builder.addCase(fetchMessage.rejected, (state, action) => {
			state.conversation.messages = state.conversation.messages.filter(
				({ requestId }) => requestId === action.meta.requestId
			);
		});
		builder.addCase(fetchMessage.pending, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg) {
					state.conversation.messages[i].requestId = action.meta.requestId;
					return;
				}
			}
		});

		// deleteMessage
		builder.addCase(deleteMessage.fulfilled, (state, action) => {
			state.conversation.next = state.conversation.user ? getNow() : undefined;
			// remove the message from state
			state.conversation.messages = state.conversation.messages.filter(({ _id }) => _id !== action.payload._id);
		});
		builder.addCase(deleteMessage.rejected, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg) {
					// clear the request id and set the error and clear deleting flag
					state.conversation.messages[i].requestId = undefined;
					state.conversation.messages[i].error = action.error;
					state.conversation.messages[i].deleting = undefined;
					return;
				}
			}
			const now = getNow();
			state.conversation.next = addMilliseconds(now, getNextInterval(now));
		});
		builder.addCase(deleteMessage.pending, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg) {
					// set request id and deleting flag
					state.conversation.messages[i].requestId = action.meta.requestId;
					state.conversation.messages[i].deleting = true;
					return;
				}
			}
			const now = getNow();
			state.conversation.next = addMilliseconds(now, getNextInterval(now));
		});

		// trackRead
		builder.addCase(trackReadMessage.fulfilled, (state, action) => {
			// remove the message from state
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.payload._id) {
					state.conversation.messages[i].read = action.payload.read;
					return;
				}
			}
		});
		builder.addCase(trackReadMessage.rejected, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg) {
					// clear the request id
					state.conversation.messages[i].requestId = undefined;
					state.conversation.messages[i].error = action.error;
					state.conversation.messages[i].read = '';
					return;
				}
			}
		});
		builder.addCase(trackReadMessage.pending, (state, action) => {
			for (let i = 0; i < state.conversation.messages.length; i++) {
				if (state.conversation.messages[i]._id === action.meta.arg) {
					// set request id
					state.conversation.messages[i].requestId = action.meta.requestId;
					state.conversation.messages[i].read = getNow();
					return;
				}
			}
		});
	}
});

export const { reportMessage, unreportMessage, setUser } = messagesSlice.actions;

export default messagesSlice.reducer;

export function computeTotalUnread(state: MessagesState) {
	if (state.conversation.user) {
		const conversation = state.summary.conversations.find(({ user }) => user._id === state.conversation.user);
		if (conversation) {
			return state.summary.totalUnread - conversation.unreadCount;
		}
	}
	return state.summary.totalUnread;
}
